<template>
  <div class="add-form-wrapper fade-in">
    <!-- ===== ROW 1: PAGE HEADER ===== -->
    <div class="page-header">
      <div class="header-left">
        <h2>🧩 Create New Routing Form</h2>
        <p>Design intelligent routing forms with a live preview experience.</p>
      </div>
      <div class="header-right">
        <button class="btn-help">
          <i class="bx bx-help-circle"></i> Help
        </button>
      </div>
    </div>

    <!-- ===== ROW 2: FORM BUILDER ===== -->
    <div class="form-builder">
      <!-- LEFT PANEL: FORM CREATOR -->
      <div class="left-panel">
        <div class="card">
          <h3><i class="bx bx-customize"></i> Form Setup</h3>
          <p class="text-muted">Fill in form details and define input fields.</p>

          <!-- Upload -->
          <!-- <div class="form-group upload">
            <label>Upload Form Image</label>
            <div class="upload-box" @click="triggerUpload">
              <input type="file" ref="fileInput" @change="setImage" hidden />
              <i class="bx bx-image-add"></i>
              <span v-if="!form.image">Click to upload</span>
              <img v-if="form.image" :src="form.image" />
            </div>
          </div> -->

          <!-- Basic Info -->
          <div class="form-group" style="margin-top: 1rem;">
            <label>Form Name</label>
            <input v-model="form.name" placeholder="Enter form name" />
          </div>

          <div class="form-group">
            <label>Description</label>
            <textarea
              v-model="form.desc"
              placeholder="Short description about this form"
              rows="2"
            ></textarea>
          </div>

          <!-- Fields Builder -->
          <div class="form-builder-header">
            <h4>Form Fields</h4>
          </div>

          <transition-group name="list-fade" tag="div" class="field-list">
            <div v-for="(field, i) in form.fields" :key="i" class="field-card">
              <div class="field-header">
                <div class="drag-icon"><i class="bx bx-grid-vertical"></i></div>
                <input
                  v-model="field.title"
                  class="field-title"
                  placeholder="Field Label"
                />
                <select v-model="field.type" @change="toggleOptions(i)">
                  <option value="Textfield">Textfield</option>
                  <option value="Email">Email</option>
                  <option value="Number">Number</option>
                  <option value="Mobile">Mobile</option>
                  <option value="Textarea">Textarea</option>
                  <option value="Options">Options</option>
                  <option value="File">File</option>
                </select>
                <button class="btn-icon remove" @click="removeField(i)">
                  <i class="bx bx-trash"></i>
                </button>
              </div>

              <transition name="fade-smooth">
                <div v-if="field.showOptions" class="form-group options-group">
                  <label>Options (comma separated)</label>
                  <input
                    v-model="field.options"
                    placeholder="Example: Option 1, Option 2"
                  />
                </div>
              </transition>
            </div>
          </transition-group>

          <div class="field-actions">
            <button class="btn-black full" @click="addField">
              <i class="bx bx-plus"></i> Add New Field
            </button>
          </div>

          <div class="form-group" style="margin-top: 1rem;">
            <label>Status</label>
            <select v-model="form.status">
              <option>Active</option>
              <option>Inactive</option>
            </select>
          </div>

          <button
            class="btn-black full mt-2"
            :disabled="isSubmitting"
            @click="saveForm"
          >
            <span v-if="isSubmitting">
              <i class="bx bx-loader-alt bx-spin"></i> Saving...
            </span>
            <span v-else>Save Form</span>
          </button>
        </div>
      </div>

      <!-- RIGHT PANEL: LIVE PREVIEW -->
      <div class="right-panel">
        <div class="card preview-card">
          <h3><i class="bx bx-show"></i> Live Preview</h3>
          <p class="text-muted">See how your routing form will appear.</p>

          <div class="preview-body">
            <h4>{{ form.name || "Form Title" }}</h4>
            <p>{{ form.desc || "Form description will appear here..." }}</p>

            <div class="form-preview" v-for="(field, i) in form.fields" :key="i">
              <label>{{ field.title }}</label>

              <input
                v-if="['Textfield', 'Email', 'Number', 'Mobile', 'File'].includes(field.type)"
                :type="field.type === 'File' ? 'file' : 'text'"
              />

              <textarea v-if="field.type === 'Textarea'"></textarea>

              <select v-if="field.type === 'Options'">
                <option
                  v-for="(opt, j) in (field.options || '').split(',')"
                  :key="j"
                >
                  {{ opt.trim() }}
                </option>
              </select>
            </div>

            <button class="btn-black full mt-2">Submit Form</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>


<script setup>
import { ref, onMounted } from "vue";
import { useToast } from "vue-toastification";
import { createForm } from "@/apis/formapis";
import { useRouter,useRoute } from "vue-router";
const reservedFields = ["full name", "email", "mobile","name"];

const isEditMode = ref(false);

// === Instances ===
const router = useRouter();
const toast = useToast();
const route = useRoute();

// === State ===
// const fileInput = ref(null);
const form = ref({
  id:"",
  name: "",
  desc: "",
  image: "",
  status: "Active",
  fields: [{ title: "Your Filed Title", type: "Textfield", options: "", showOptions: false }],
});
const isSubmitting = ref(false);
const ptype = ref("routing_form");

// === File Upload ===
// const triggerUpload = () => fileInput.value.click();
// const setImage = (e) => {
//   const file = e.target.files[0];
//   if (file) {
//     const reader = new FileReader();
//     reader.onload = () => (form.value.image = reader.result);
//     reader.readAsDataURL(file);
//   }
// };

// === Field Management ===
const addField = () => {
  form.value.fields.push({ title: "", type: "Textfield", options: "", showOptions: false });
};
const removeField = (i) => {
  form.value.fields.splice(i, 1);
};
const toggleOptions = (i) => {
  form.value.fields[i].showOptions = form.value.fields[i].type === "Options";
};

// === Validation Helper ===
const validateForm = () => {
  if (!form.value.name?.trim()) {
    toast.error("Please enter a form name.");
    return false;
  }
  if (!form.value.desc?.trim()) {
    toast.error("Please enter a form description.");
    return false;
  }
  if (!form.value.fields.length) {
    toast.error("Please add at least one field.");
    return false;
  }
  const emptyField = form.value.fields.find(
      (f) => !f.title.trim() || reservedFields.includes(f.title.trim().toLocaleLowerCase())
    );

    if (emptyField) {
      toast.error("Invalid field title. Reserved names are not allowed.");
      return false;
    }
  return true;
};

// === Save Form ===
const saveForm = async () => {
  if (!validateForm()) return;

  try {
    isSubmitting.value = true;

    const user = JSON.parse(sessionStorage.getItem("ldata"));
    const userId = user?.user_id || "";

    // prepare data for backend
    const payload = {
      form_temp_id: form.value.id,
      form_temp_title: form.value.name,
      form_temp_desc: form.value.desc,
      form_temp_fields: JSON.stringify(form.value.fields),
      form_temp_status: form.value.status,
      form_temp_by: userId,
    };

    const res = await createForm(payload);

    if (res.status) {
      toast.success("✅ Form Created Successfully!");
      setTimeout(() => router.push("/"+ptype.value), 1200);
    } else {
      toast.error(res.success || "Failed to create form.");
    }
  } catch (err) {
    console.error("saveForm error:", err);
    toast.error("Something went wrong while saving form.");
  } finally {
    isSubmitting.value = false;
  }
};



onMounted(() => {
   try{
      const rtype = route.query.rtype;

        if (rtype && rtype.length>0) {
          ptype.value = rtype;
        }

    }
    catch(e){
      console.log("sdfsad",e);
    }
  if (sessionStorage.getItem("form_data") && sessionStorage.getItem("form_data").length>0) {
    try {
      const parsed = JSON.parse(sessionStorage.getItem("form_data"));
      isEditMode.value = true;

      // Prefill form data safely
      form.value.id = parsed.id || "";
      form.value.name = parsed.title || "";
      form.value.desc = parsed.desc || "";
      form.value.image = parsed.image || "";
      form.value.status = parsed.status || "Active";

      // alert(sessionStorage.getItem("form_data")+"");

      // Handle dynamic fields (convert backend stringified fields if needed)
      if (parsed.fields.length>0) {
        form.value.fields = parsed.fields;
      } else {
        form.value.fields = [];
      }

    } catch (err) {
      console.error("❌ Error loading form data:", err);
    }
  } else {
    console.log("🆕 New Form Mode");
  }
});

</script>


<style scoped>
.add-form-wrapper {
  background: #fff;
  border-radius: 1rem;
  padding: 2rem;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.05);
  font-family: "Inter", sans-serif;
}

/* ===== HEADER ===== */
.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
}
.header-left h2 {
  color: #111827;
  font-weight: 700;
  margin-bottom: 0.3rem;
}
.header-left p {
  color: #6b7280;
}
.btn-help {
  background: #111827;
  color: #fff;
  border: none;
  padding: 0.6rem 1.2rem;
  border-radius: 8px;
  cursor: pointer;
  transition: 0.3s;
}
.btn-help:hover {
  opacity: 0.9;
}

/* ===== LAYOUT ===== */
.form-builder {
  display: flex;
  gap: 2rem;
}
.left-panel {
  width: 45%;
}
.right-panel {
  width: 55%;
}
.card {
  background: #f9fafb;
  border-radius: 1rem;
  padding: 1.5rem;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.05);
}
.card h3 {
  margin-bottom: 0.3rem;
  color: #111827;
}
.text-muted {
  color: #6b7280;
  font-size: 0.9rem;
}

/* ===== FORM ELEMENTS ===== */
.form-group {
  margin-bottom: 1.2rem;
}
label {
  font-weight: 600;
  color: #374151;
  display: block;
  margin-bottom: 0.3rem;
}
input,
textarea,
select {
  width: 100%;
  padding: 0.7rem 0.9rem;
  border-radius: 10px;
  border: 1px solid #d1d5db;
  font-size: 0.9rem;
  outline: none;
  transition: 0.3s;
  background: #fff;
}
input:focus,
textarea:focus,
select:focus {
  border-color: #111827;
  box-shadow: 0 0 0 2px rgba(17, 24, 39, 0.1);
}
textarea {
  resize: none;
}

/* Upload Box */
.upload-box {
  border: 2px dashed #d1d5db;
  border-radius: 12px;
  text-align: center;
  padding: 2rem;
  cursor: pointer;
  transition: all 0.3s ease;
}
.upload-box:hover {
  border-color: #111827;
}
.upload-box img {
  width: 100px;
  border-radius: 8px;
  margin-top: 0.5rem;
}

/* ===== FORM FIELDS ===== */
.field-card {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 1rem;
  margin-bottom: 0.9rem;
  position: relative;
  transition: 0.3s;
}
.field-card:hover {
  box-shadow: 0 3px 12px rgba(0, 0, 0, 0.06);
}
.field-header {
  display: grid;
  grid-template-columns: auto 1fr 1fr auto;
  align-items: center;
  gap: 10px;
}
.field-header .drag-icon {
  font-size: 1.2rem;
  color: #9ca3af;
  cursor: grab;
}
.field-title {
  border: 1px solid #d1d5db;
  border-radius: 8px;
  padding: 0.6rem;
}
.field-header select {
  padding: 0.6rem;
  border-radius: 8px;
  border: 1px solid #d1d5db;
}
.btn-icon.remove {
  background: transparent;
  border: none;
  color: #dc2626;
  font-size: 1.2rem;
  cursor: pointer;
  transition: 0.3s;
}
.btn-icon.remove:hover {
  color: #b91c1c;
}
.options-group {
  margin-top: 0.6rem;
  padding-left: 2.3rem;
  border-left: 2px solid #f3f4f6;
}

/* ===== BUTTONS ===== */
.btn-black {
  background: #111827;
  color: #fff;
  border: none;
  padding: 0.7rem 1.3rem;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 500;
  transition: 0.3s;
}
.btn-black:hover {
  opacity: 0.9;
}
.btn-black.full {
  width: 100%;
  text-align: center;
}

/* ===== PREVIEW ===== */
.preview-card {
  background: #fff;
  padding: 2rem;
  border-radius: 1rem;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.05);
}
.preview-body {
  background: #f9fafb;
  padding: 1.5rem;
  border-radius: 10px;
}
.form-preview {
  margin-bottom: 1rem;
}
.form-preview label {
  display: block;
  margin-bottom: 0.3rem;
}

/* ===== ANIMATIONS ===== */
.fade-in {
  animation: fadeIn 0.5s ease;
}
.fade-smooth-enter-active,
.fade-smooth-leave-active {
  transition: all 0.3s ease;
}
.fade-smooth-enter-from,
.fade-smooth-leave-to {
  opacity: 0;
  transform: translateY(-5px);
}
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* ===== RESPONSIVE ===== */
@media (max-width: 992px) {
  .form-builder {
    flex-direction: column;
  }
  .left-panel,
  .right-panel {
    width: 100%;
  }
}

/* === 📱 MOBILE VIEW FIXES FOR ADD FORM WRAPPER === */
@media (max-width: 768px) {
  /* Page Header */
  .page-header {
    flex-direction: column !important;
    align-items: flex-start !important;
    gap: 0.8rem;
  }

  .header-left h2 {
    font-size: 1.4rem;
  }

  .header-left p {
    font-size: 0.9rem;
  }

  .header-right {
    width: 100%;
    display: flex;
    justify-content: flex-end;
  }

  .btn-help {
    width: auto;
    font-size: 0.85rem;
    padding: 0.5rem 1rem;
  }

  /* Form Builder Panels */
  .form-builder {
    flex-direction: column !important;
    gap: 1.5rem !important;
  }

  .left-panel,
  .right-panel {
    width: 100% !important;
  }

  /* Card Spacing */
  .card {
    padding: 1.2rem !important;
  }

  /* Upload Box */
  .upload-box {
    padding: 1.2rem !important;
  }
  .upload-box img {
    width: 80px !important;
  }

  /* Field Layout */
  .field-header {
    grid-template-columns: 1fr 1fr;
    gap: 0.6rem;
  }
  .field-header .drag-icon {
    display: none;
  }
  .field-header select {
    width: 100%;
  }

  .field-title {
    width: 100%;
  }

  .btn-icon.remove {
    position: absolute;
    top: 8px;
    right: 8px;
    font-size: 1rem;
  }

  /* Form Buttons */
  .btn-black.full {
    width: 100% !important;
    font-size: 0.95rem;
  }

  /* Live Preview */
  .preview-card {
    padding: 1.2rem !important;
  }

  .preview-body {
    padding: 1rem !important;
  }

  .preview-body h4 {
    font-size: 1.1rem;
  }

  /* Form Fields in Preview */
  .form-preview label {
    font-size: 0.9rem;
  }

  .form-preview input,
  .form-preview textarea,
  .form-preview select {
    font-size: 0.9rem;
    padding: 0.6rem 0.8rem;
  }
}

/* 📱 Extra Small Devices (below 480px) */
@media (max-width: 480px) {
  .add-form-wrapper {
    padding: 1.2rem !important;
  }

  .header-left h2 {
    font-size: 1.2rem !important;
  }

  .upload-box {
    padding: 1rem !important;
  }

  .btn-help {
    width: 100%;
    text-align: center;
  }

  .btn-black.full {
    font-size: 0.85rem !important;
    padding: 0.6rem 1rem !important;
  }

  .field-card {
    padding: 0.8rem !important;
  }

  .form-preview label {
    font-size: 0.85rem;
  }
}
/* ==========================================================
   📱 ADDITIONAL RESPONSIVE FIXES — CREATE NEW ROUTING FORM
   ========================================================== */

/* === Ensure no horizontal overflow on any device === */
.add-form-wrapper {
  overflow-x: hidden;
}

/* === Slightly reduce padding for smaller tablets === */
@media (max-width: 992px) {
  .add-form-wrapper {
    padding: 1.6rem !important;
  }

  .card {
    padding: 1.4rem !important;
  }
}

/* === Improve spacing & readability on tablets and small laptops === */
@media (max-width: 768px) {
  .form-builder {
    flex-direction: column;
    gap: 1.4rem;
  }

  .left-panel,
  .right-panel {
    width: 100%;
  }

  .field-list {
    display: flex;
    flex-direction: column;
    gap: 0.8rem;
  }

  /* Field header alignment fix */
  .field-header {
    display: flex;
    flex-wrap: wrap;
    gap: 0.6rem;
  }

  .field-header input,
  .field-header select {
    flex: 1 1 48%;
  }

  /* Prevent overlapping trash icon */
  .btn-icon.remove {
    position: absolute;
    top: 6px;
    right: 6px;
    background: #fff;
    border-radius: 50%;
    width: 26px;
    height: 26px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  /* Upload section scaling */
  .upload-box {
    padding: 1.2rem;
    text-align: center;
  }

  .upload-box img {
    width: 80px;
    height: auto;
  }

  /* Button layout */
  .btn-black.full {
    width: 100%;
    text-align: center;
    font-size: 0.95rem;
  }

  /* Live preview section spacing */
  .preview-body {
    padding: 1.2rem;
  }

  .preview-body h4 {
    font-size: 1.1rem;
  }

  .preview-body p {
    font-size: 0.9rem;
  }

  .form-preview label {
    font-size: 0.9rem;
  }

  .form-preview input,
  .form-preview textarea,
  .form-preview select {
    padding: 0.6rem 0.8rem;
    font-size: 0.9rem;
  }
}

/* === Compact mobile layout (under 480px) === */
@media (max-width: 480px) {
  .add-form-wrapper {
    padding: 1rem !important;
  }

  .page-header {
    flex-direction: column;
    gap: 0.6rem;
  }

  .header-left h2 {
    font-size: 1.2rem !important;
  }

  .header-left p {
    font-size: 0.85rem !important;
  }

  .btn-help {
    width: 100%;
    font-size: 0.8rem;
    padding: 0.5rem 0.9rem;
  }

  .field-card {
    padding: 0.8rem;
  }

  .field-header input,
  .field-header select {
    width: 100%;
  }

  .preview-card {
    padding: 1rem;
  }

  .preview-body {
    padding: 0.9rem;
  }

  .btn-black.full {
    font-size: 0.85rem;
    padding: 0.6rem 1rem;
  }
}

/* === Prevent long field titles or inputs from breaking layout === */
.field-title,
input,
textarea,
select {
  word-break: break-word;
  max-width: 100%;
  box-sizing: border-box;
}

/* === Smooth resizing transitions for better UX === */
@media (prefers-reduced-motion: no-preference) {
  .add-form-wrapper,
  .form-builder,
  .card {
    transition: all 0.25s ease-in-out;
  }
}

/* === Smooth Transitions for Better UX === */
.add-form-wrapper,
.form-builder,
.card,
.field-card,
input,
select,
textarea,
button {
  transition: all 0.25s ease-in-out;
}

/* === Fix for Upload Box Image Preview Overflow === */
.upload-box img {
  max-width: 100%;
  max-height: 120px;
  object-fit: cover;
  display: block;
  margin: 0.6rem auto 0;
  border-radius: 10px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
}

/* === Add Hover Effect for Buttons === */
.btn-black:hover,
.btn-help:hover {
  transform: translateY(-1px);
  box-shadow: 0 3px 10px rgba(17, 24, 39, 0.2);
}

/* === Fix: Ensure Add New Field Button has consistent spacing === */
.field-actions {
  margin-top: 1rem;
  display: flex;
  justify-content: center;
}
.field-actions .btn-black {
  width: 100%;
}

/* === Fix: Field Dropdown & Title Alignment === */
.field-header input.field-title {
  padding: 0.6rem 0.8rem;
  font-size: 0.9rem;
}
.field-header select {
  font-size: 0.9rem;
}

/* === Prevent Misalignment for Remove Button === */
.btn-icon.remove {
  position: relative;
  top: 0;
  right: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* === Live Preview Card Enhancements === */
.preview-card {
  border: 1px solid #e5e7eb;
}
.preview-body {
  background: #f9fafb;
  padding: 1.5rem;
  border-radius: 0.8rem;
}
.preview-body h4 {
  color: #111827;
  font-weight: 600;
  margin-bottom: 0.4rem;
}
.preview-body p {
  color: #6b7280;
  margin-bottom: 1rem;
}

/* === Loading Spinner Animation === */
.bx-loader-alt {
  vertical-align: middle;
  font-size: 1.1rem;
  margin-right: 6px;
}

/* === Improve Mobile Padding === */
@media (max-width: 768px) {
  .add-form-wrapper {
    padding: 1.4rem !important;
  }

  .field-card {
    margin-bottom: 1rem;
  }

  .preview-body {
    padding: 1.2rem !important;
  }

  .btn-black.full {
    padding: 0.8rem !important;
    font-size: 0.9rem !important;
  }
}

/* === Compact Mobile Optimization === */
@media (max-width: 480px) {
  .field-header {
    flex-direction: column;
    gap: 0.4rem;
  }

  .upload-box {
    padding: 1rem;
    font-size: 0.85rem;
  }

  .btn-black.full {
    font-size: 0.85rem;
    padding: 0.6rem 1rem;
  }

  .field-card {
    padding: 0.9rem;
  }

  .preview-card {
    padding: 1.1rem;
  }

  .form-preview input,
  .form-preview select,
  .form-preview textarea {
    padding: 0.5rem 0.8rem;
    font-size: 0.9rem;
  }
}

/* === Accessibility: Focus Ring for Inputs & Buttons === */
input:focus,
textarea:focus,
select:focus,
button:focus {
  outline: 2px solid var(--primary, #111827);
  outline-offset: 2px;
}

/* === Smooth Fade for Toast-Like Popups === */
.fade-smooth-enter-active,
.fade-smooth-leave-active {
  transition: opacity 0.3s, transform 0.3s;
}
.fade-smooth-enter-from,
.fade-smooth-leave-to {
  opacity: 0;
  transform: translateY(5px);
}
</style>

